import { cn } from '@spiaggeit/spit-ui'
import { decode } from 'html-entities'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from '@/hooks/store.ts'

import { LicenseBookingConditionsLabelStyle } from '@/models/license'
import { licenseSlice } from '@/store/licenseSlice'
import { selectBookingPolicyLabelString } from '@/store/licenseSlice/selectors/selectBookingPolicyLabelString'
import {
  REFUND_POLICY,
  REFUND_POLICY_DAYS_MAP,
} from '@/utils/bookingRefundPolicy'
import { RefundPolicyDialog } from '../RefundPolicyDialog'
import { RefundPolicyIcon } from './RefundPolicyIcon'

export const RefundPolicy = () => {
  const { t } = useTranslation()

  const license = useAppSelector(licenseSlice.selectors.license)
  const isBookingRefundable = useAppSelector(
    licenseSlice.selectors.isBookingRefundable
  )
  const bookingPolicyLabelString = useAppSelector(
    selectBookingPolicyLabelString
  )

  const refundPolicyString = useMemo(() => {
    if (bookingPolicyLabelString !== null) {
      return bookingPolicyLabelString
    }

    if (
      !isBookingRefundable ||
      !license?.bookingRefundPolicy ||
      license.bookingRefundPolicy === REFUND_POLICY.NEVER
    ) {
      return decode(t('map.elementDetail.refundPolicy.negative'))
    }

    if (license.bookingRefundPolicy === REFUND_POLICY.DAY_BEFORE) {
      return decode(
        t('map.elementDetail.refundPolicy.dayBefore', {
          licenseName: license.name,
        })
      )
    }

    return decode(
      t('map.elementDetail.refundPolicy.positive', {
        count: REFUND_POLICY_DAYS_MAP[license.bookingRefundPolicy],
        licenseName: license.name,
      })
    )
  }, [license, isBookingRefundable, t])

  if (!license) return null

  return (
    <div
      className={cn(
        'rounded-sm border p-1',
        !license.bookingConditionsLabelStyle
          ? {
              'border-green-100 bg-green-25': isBookingRefundable,
              'border-red-200 bg-red-50': !isBookingRefundable,
            }
          : {
              'border-green-100 bg-green-25':
                license.bookingConditionsLabelStyle ===
                LicenseBookingConditionsLabelStyle.INFO,
              'border-red-200 bg-red-50':
                license.bookingConditionsLabelStyle ===
                LicenseBookingConditionsLabelStyle.DANGER,
              'border-yellow-200 bg-yellow-50':
                license.bookingConditionsLabelStyle ===
                LicenseBookingConditionsLabelStyle.WARNING,
            }
      )}
      role="status"
    >
      <div className="flex gap-1">
        <RefundPolicyIcon
          bookingConditionsLabelStyle={license.bookingConditionsLabelStyle}
          isBookingRefundable={isBookingRefundable ?? false}
        />

        <div className="flex flex-col items-start gap-1">
          <span className="text-sm">{refundPolicyString}</span>

          <RefundPolicyDialog
            bookingRefundPolicy={license.bookingRefundPolicy}
            licenseName={license.name}
            trigger={
              <button className="text-xs font-bold">
                <span aria-hidden="true">+</span> {t('common.moreInfo')}
              </button>
            }
          />
        </div>
      </div>
    </div>
  )
}
